/* eslint-disable no-nested-ternary */
import React from 'react';
import NProgress from 'nprogress';
import { useMutation } from '@apollo/client';
import { UPDATE_USER } from '../../graphql/mutation';
import { Company, DocumentModel, Multimedia, User } from '../../models';
import {
  UserFormFields,
  reducer as userReducer,
} from '../shared/Forms/GeneralUserFormFields';
import {
  CompanyFormFields,
  reducer as companyReducer,
} from '../shared/Forms/CompanyFormFields';
import { validateCompanyForm } from '../../lib/formValidations';
import { useNotify, useUser } from '../../hooks';
import { validateUserForm } from '../../lib/formValidations/validateUserForm';
import { roles } from '../../static';
import FormButtons from '../shared/Forms/FormButtons';
import DocumentForm from '../shared/document/DocumentForm';

interface ProfileFormProps {
  user: User;
}

function ProfileForm({ user }: ProfileFormProps) {
  const notify = useNotify();
  const [_, setUserContext] = useUser();
  const [disabled, setDisabled] = React.useState(false);
  const [updateCompanyMutation] = useMutation<{
    updateUser: User;
  }>(UPDATE_USER);

  const [documents, setDocuments] = React.useState<
    Array<Partial<DocumentModel>>
  >(
    user?.userType === roles.admin
      ? (user?.photo as Multimedia)?.src
        ? [{ src: (user?.photo as Multimedia)?.src }]
        : []
      : (user?.company as Company)?.logo?.src
      ? [{ src: (user?.company as Company)?.logo?.src ?? '' }]
      : []
  );
  // form states
  const [userState, userDispatch] = React.useReducer(userReducer, user ?? {});
  const [companyState, companyDispatch] = React.useReducer(
    companyReducer,
    user ? { ...(user.company as Company) } : {}
  );

  // functions
  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      NProgress.start();
      // disabled validation
      if (disabled) return;
      setDisabled(true);
      // validate form
      if (user.userType !== roles.admin) {
        const companyError = validateCompanyForm({
          ...companyState,
        });
        if (companyError) return notify(companyError, 'error');
      }
      const userError = validateUserForm({
        user: { ...userState },
        update: true,
      });
      if (userError) return notify(userError, 'error');
      // build object
      const userData = {
        ...userState,
        ...(user.userType !== roles.admin
          ? {
              company: {
                ...companyState,
                logo: (documents[0]?.src as string)
                  ? { src: documents[0]?.src as string, __typename: undefined }
                  : null,
                __typename: undefined,
              } as Company,
            }
          : {}),
        __typename: undefined,
      };

      delete (userData.company as Company)?.addresses;
      delete userData?.firstLogin;

      if (user.userType === roles.admin) {
        const document = documents[0];
        userData.photo = document?.src ? { src: document.src as string } : null;
        delete userData?.company;
      }
      if (user.userType !== roles.client) {
        delete userData.client;
      }
      if (user?._id) {
        delete (userData.company as Company)?.shopCart;
        await updateProfile(userData);
      }
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      NProgress.done();
      setDisabled(false);
    }
  };

  const updateProfile = async (companyData: Partial<User>) => {
    delete companyData.currentSubscription;
    delete companyData.discountCoupons;
    delete (companyData?.company as Company)?.companyDetails;
    const { data } = await updateCompanyMutation({
      variables: {
        data: {
          ...companyData,
          _id: user._id,
          __typename: undefined,
        },
      },
    });
    if (data?.updateUser?._id) {
      notify('Perfil actualizado con éxito', 'success');
      setUserContext(data?.updateUser);
    } else {
      notify('Ha ocurrido un error al actualizar el perfil', 'error');
    }
  };

  return (
    <form
      method="POST"
      onSubmit={onSubmit}
      className="flex flex-col w-full space-y-10"
    >
      {user.userType !== 'admin' ? (
        <CompanyFormFields
          dispatch={companyDispatch}
          state={companyState}
          userType={user.userType}
          header={{
            title: 'Información de la compañia',
          }}
        />
      ) : null}
      <UserFormFields
        dispatch={userDispatch}
        state={userState}
        userType={user.userType}
        header={{
          title: 'Información del usuario',
        }}
      />
      <DocumentForm
        documents={documents}
        updateURLs={setDocuments}
        maximumAmount={1}
      />
      <FormButtons
        type="update"
        active={user.active}
        disabled={disabled}
        onSubmit={onSubmit}
      />
    </form>
  );
}

export default ProfileForm;
